import Header from "./Header.jsx";
import {useState, useEffect, Suspense} from "react";
import useSound from 'use-sound';
import emergSound from "../sound/emergency_tone.mp3"
import ApiRequester from "./ApiRequester.jsx";
import {
    durationToNow, sortFunc, setSoundsMute, isBannerPresent
} from "../libs.jsx";
import {ToastContainer} from "react-toastify";
import Footer from "./Footer.jsx";
import TableHeaders from "./component-assets/TableHeaders.jsx";
import CampusDropDown from "./component-assets/CampusDropDown.jsx";
import AlertSyncStatus from "./component-assets/AlertSyncStatus.jsx";
import TableBody from "./component-assets/TableBody.jsx";
import Navigation from "./Navigation.jsx";
import SessionReload from "./component-assets/SessionReload.jsx";
import {Link} from "react-router-dom";
import Button from "./component-assets/Button.jsx";
import TableSkeleton from "./component-assets/skeletons/TableSkeleton.jsx";
import {CSVLink} from "react-csv";
import { useNotifications } from "../contexts/NotificationContext.jsx";
import { getSession } from "../services/session";


const CurrentCloudAlerts = (props) => {

    const { activeCloudAlarms, isConnected, connectionError, isDataStale, registerAlertViewer, unregisterAlertViewer } = useNotifications();

    // Register as alert viewer so the provider enables periodic reconciliation while this page is mounted
    useEffect(() => { registerAlertViewer(); return () => unregisterAlertViewer(); }, [registerAlertViewer, unregisterAlertViewer]);

    let dData, headerData, maintHeaderData, csvHeaders, maintCSVHeaders;

    const [emergencySound] = useSound(emergSound, {volume: .5});

    headerData = [
        {label: "Event Start", key: "event_datetime_utc", align: "center", type: "datetime-utc",
            border: {enabled: false, borderType: "left", borderColorKey: "event_category", borderWidth: "6px"}},
        {label: "Duration", key: "event_datetime_utc", align: "center", type: "duration"},
        {label: "Alert Category / Type", key: ["event_category", "event_type"], align: "center", type: "stack-muted"},
        {label: "Device / Serial", key: ["device_name", "manufacture_uuid"], align: "center",
            link: {enabled: true, type: "conditional-stack", conditionKey: "manufacture_id",
                condition: {
                    1: {linkto: "/profiles/inovonics", linkkey: "device_id"},
                    4: {linkto: "/profiles/palcare", linkkey: "device_id"}
                }}},
        {label: "Description", key: "device_description", align: "center"},
        {label: "Area", key: "area_name", align: "center",
            link: {enabled: true, type: "standard", linkto: "/profiles/area", linkkey: "area_id"}},
        {label: "Resident", key: "resident_name", align: "center",
            link: {enabled: true, type: "standard", linkto: "/profiles/resident", linkkey: "resident_id"}},
        {label: "Accepted By / Time", key: ["accepted_display", "accepted_datetime_utc"], align: "center",
            link: {type: "stack", enabled: true, linkto: "/profiles/user", linkkey: "accepted_user_id"}},
        {label: "Device Cleared", key: "ack_datetime_utc", align: "center", type: "datetime-utc"},
        {label: "Status", key: "status", type: "alert-status", align: "center",
            status: {className: {Waiting: "text-warning", Overdue: "text-danger", Accepted: "text-primary",
                                 Cleared: "text-primary", Arrived: "text-primary", Default: "text-primary"}},
            popover: {body: {Waiting: "Waiting for alert to be accepted or cleared if accept feature is N/A for device.",
                             Overdue: "Alert has been active over 20 minutes.",
                             Accepted: "Alert has been accepted by a staff member but the device is still in alarm state.",
                             Cleared: "Device has been cleared. If this device is listed for AFR in system settings, it must be accepted and finished through the mobile app.",
                             Arrived: "Staff has accepted the alert and the device's alarm state has been cleared. Alert must be finished to close out.",
                             Default: ""
                            }
                      }
        }
    ];

    maintHeaderData = [
        {label: "Detected", key: "event_datetime_utc", align: "center", type: "datetime-utc"},
        {label: "Duration", key: "event_datetime_utc", align: "center", type: "duration"},
        {label: "Condition", key: "condition", align: "center"},
        {label: "Device / Serial", key: ["device_name", "manufacture_uuid"], align: "center",
            link: {enabled: true, type: "conditional-stack", conditionKey: "manufacture_id",
                condition: {
                    1: {linkto: "/profiles/inovonics", linkkey: "device_id"},
                    4: {linkto: "/profiles/palcare", linkkey: "device_id"}
                }}},
        {label: "Area", key: "area_name", align: "center",
            link: {enabled: true, type: "standard", linkto: "/profiles/area", linkkey: "area_id"}},
        {label: "Resident", key: "resident_name", align: "center",
            link: {enabled: true, type: "standard", linkto: "/profiles/resident", linkkey: "resident_id"}}
    ]

    csvHeaders = [
        {label: "Event Time", key: "event_datetime_utc"},
        {label: "Alert Category", key: "event_category"},
        {label: "Alert Type", key: "event_type"},
        {label: "Device Name", key: "device_name"},
        {label: "Device Description", key: "device_description"},
        {label: "Device Maker", key: "manufacture_name"},
        {label: "Device Unique ID", key: "manufacture_uuid"},
        {label: "Area", key: "area_name"},
        {label: "Resident First Name", key: "resident_first_name"},
        {label: "Resident Last Name", key: "resident_last_name"},
        {label: "Accepted User", key: "accepted_username"},
        {label: "Accepted User Display Name", key: "accepted_display"},
        {label: "Accepted Time", key: "accepted_datetime_utc"},
        {label: "Device Cleared Time", key: "ack_datetime_utc"},
        {label: "Vendor Invoked", key: "vendor"},
        {label: "Device Cleared Time", key: "ack_datetime_utc"}
    ];

    maintCSVHeaders = [
        {label: "Detected (UTC)", key: "event_datetime_utc"},
        {label: "Condition", key: "condition"},
        {label: "Device Name", key: "device_name"},
        {label: "Device Serial #", key: "manufacture_uuid"},
        {label: "Area", key: "area_name"},
        {label: "Resident", key: "resident_name"}
    ]

    if (getSession() != null){
        dData = getSession();
    }

    const [maintLoaded, setMaintLoaded] = useState(false);
    const [lowBatData, setLowBatData] = useState([]);
    const [otherData, setOtherData] = useState([]);
    const [lostData, setLostData] = useState([]);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [activeData, setActiveData] = useState([]);
    const [emergRows, setEmergRows] = useState([]);
    const [lowBatRows, setLowBatRows] = useState([]);
    const [lostRows, setLostRows] = useState([]);
    const [otherRows, setOtherRows] = useState([]);
    const [sorter, setSorter] = useState({
        sortColumn: "event_datetime_utc",
        sortOrder: "asc"
    });
    const [maintSorter, setMaintSorter] = useState({
        sortColumn: "event_datetime_utc",
        sortOrder: "asc"
    });
    const [count, setCount] = useState(0);
    const [soundOff, setSoundOff] = useState(dData.soundsOff);
    const [soundInit, setSoundInit] = useState(true);
    const [alertKeyList, setAlertKeyList] = useState([]);
    const [campusId, setCampusId] = useState(dData.acnt);
    const [tabItem, setTabItem] = useState("emergency");
    const [fullscreen, setFullScreen] = useState(false);
    const [soundOn, setSoundOn] = useState(true);
    const [csvLink, setLink] = useState({
        filename: "alarms.csv",
        headers: [],
        data: []
    });
    const [csvButton, setCSVButton] = useState(
    <CSVLink {...csvLink} uFEFF={false}>
            <Button text={"Export"} icon={true} iconClass={"fe fe-download me-2"} class={"btn btn-primary ms-2 px-4 mt-1"}/>
        </CSVLink> ? props?.toFetch === "maintenance" : ""
    )

    useEffect(() => {
        let interval;
        interval = null;
        interval = setInterval(() => {
             setCount(count => count - 1);
        }, 1000);
        if (count <= 0) {
            ApiRequester({reqEndpoint: "myfetch", pKey: "campus_id", pVal: dData.acnt, "read-only": true,
                                procedure: "getdevicemaintenance", reqType: "stored"}).then(data => {
                let tmpOther = [];
                let tmpBat = [];
                let tmpLost = [];
                for (let ie=0; ie < data.length; ie++){
                    if (["low battery", "missing battery"].includes(data[ie].condition.toLowerCase())){
                        tmpBat.push(data[ie]);
                    }else if (["communication lost"].includes(data[ie].condition.toLowerCase())){
                        tmpLost.push(data[ie]);
                    }else{
                        tmpOther.push(data[ie]);
                    }
                }
                setOtherData(tmpOther);
                setLostData(tmpLost);
                setLowBatData(tmpBat);
                setMaintLoaded(true);
            })
            setCount((count) => 60);
        }
        return () => clearInterval(interval);
    }, [count]);

    useEffect(() => {
        if (maintLoaded) {
            setPageLoaded(true);
        }
    }, [maintLoaded]);

    useEffect(() => {
        const emergData = activeCloudAlarms || [];
        let playSound = false;
        const newKeys = [];
        for (let i = 0; i < emergData.length; i++) {
            if (!alertKeyList.includes(emergData[i]?.event_id) && !newKeys.includes(emergData[i]?.event_id)) {
                newKeys.push(emergData[i]?.event_id);
                if (!soundInit) {
                    playSound = true;
                }
            }
        }
        if (newKeys.length > 0) {
            setAlertKeyList((prevState) => {
                const items = [...prevState];
                newKeys.forEach((id) => items.push(id));
                return items;
            });
        }
        if (tabItem === "emergency") {
            const sorted = [...emergData];
            if (sorted.length > 0) {
                sorted.sort((a, b) => sortFunc(a, b, sorter.sortColumn, sorter.sortOrder));
            }
            setEmergRows(<TableBody start={0} end={sorted.length} tableData={sorted}
                                    checkbox={false} writeAccess={false} unk={"a-"} checkidkey={"event_id"}
                                    actionAppearance={() => {}} tableHeaders={headerData} editModal={() => {}}
                                    parentComponent={"Alerts"} dData={dData} checkboxlist={[]} setCheckboxId={() => {}}
                                    tzOffset={parseInt(dData?.campusData[campusId]?.tz_offset ? dData?.campusData[campusId]?.tz_offset : 0)} />);
        } else if (tabItem === "lowbat") {
            lowBatData.sort((a, b) => sortFunc(a, b, maintSorter.sortColumn, maintSorter.sortOrder));
            setLowBatRows(<TableBody start={0} end={lowBatData?.length ? lowBatData.length : 0} tableData={lowBatData}
                                checkbox={false} writeAccess={false} unk={"b-"} checkidkey={"AlarmId"}
                                actionAppearance={() => {}} tableHeaders={maintHeaderData} editModal={() => {}}
                                parentComponent={"Alerts"} dData={dData} checkboxlist={[]} setCheckboxId={() => {}}
                                tzOffset={parseInt(dData?.campusData[campusId]?.tz_offset ? dData?.campusData[campusId]?.tz_offset : 0)} />);
        }else if (tabItem === "other"){
            otherData.sort((a, b) => sortFunc(a, b, maintSorter.sortColumn, maintSorter.sortOrder));
            setOtherRows(<TableBody start={0} end={otherData?.length ? otherData.length : 0} tableData={otherData}
                                checkbox={false} writeAccess={false} unk={"o-"} checkidkey={"AlarmId"}
                                actionAppearance={() => {}} tableHeaders={maintHeaderData} editModal={() => {}}
                                parentComponent={"Alerts"} dData={dData} checkboxlist={[]} setCheckboxId={() => {}}
                                tzOffset={parseInt(dData?.campusData[campusId]?.tz_offset ? dData?.campusData[campusId]?.tz_offset : 0)} />);
        }else if (tabItem === "lost"){
            lostData.sort((a, b) => sortFunc(a, b, maintSorter.sortColumn, maintSorter.sortOrder));
            setLostRows(<TableBody start={0} end={lostData?.length ? lostData.length : 0} tableData={lostData}
                                checkbox={false} writeAccess={false} unk={"l-"}
                                actionAppearance={() => {}} tableHeaders={maintHeaderData} editModal={() => {}}
                                parentComponent={"Alerts"} dData={dData} checkboxlist={[]} setCheckboxId={() => {}}
                                tzOffset={parseInt(dData?.campusData[campusId]?.tz_offset ? dData?.campusData[campusId]?.tz_offset : 0)} />);
        }
        if (playSound && !soundOff) {
            emergencySound();
        }
        if (soundInit) {
            setSoundInit(false);
        }
    }, [activeCloudAlarms, lowBatData, lostData, otherData, maintSorter, sorter, tabItem, count]);


    const tableHeaders = <TableHeaders checkbox={false} headerData={headerData} sortOrder={sorter.sortOrder}
                                       borderBuffer={true} sortColumn={sorter.sortColumn}
                                       sorter={sorter} setSorter={setSorter} />
    const tableMaintHeaders = <TableHeaders checkbox={false} headerData={maintHeaderData} sortOrder={maintSorter.sortOrder}
                                       borderBuffer={true} sortColumn={maintSorter.sortColumn}
                                       sorter={maintSorter} setSorter={setMaintSorter} />

    const headerContent = (
        <div className={"d-flex justify-content-between"}>
            {fullscreen
                ?
                <div>
                    <Button text={"Exit Station Mode"} class={"btn btn-primary"} icon={true}
                            iconClass={"fe fe-minimize-2 me-2"} onClick={() => setFullScreen(false)}/>
                    <div className={"form-check mt-3"}>
                        <input className={"form-check-input table-checkbox"} type={"checkbox"} checked={soundOff}
                               name={"sound-on-checkbox"} id={"sound-on-checkbox"}
                               style={{width: "20px", height: "20px"}}
                               onChange={(e) => {
                                   setSoundOff(e.target.checked);
                                   setSoundsMute(dData, e.target.checked);
                               }}/>
                        <label className={"form-check-label"} htmlFor={"sound-on-checkbox"}>Mute Sounds</label>
                    </div>
                </div>
                : <div>
                    <Button text={"Enter Station Mode"} class={"btn btn-primary"} icon={true}
                            iconClass={"fe fe-maximize-2 me-2"} onClick={() => setFullScreen(true)}/>
                    <div className={"form-check mt-3"}>
                        <input className={"form-check-input table-checkbox"} type={"checkbox"} checked={soundOff}
                               name={"sound-on-checkbox"} id={"sound-on-checkbox"}
                               style={{width: "20px", height: "20px"}}
                               onChange={(e) => {
                                   setSoundOff(e.target.checked);
                                   setSoundsMute(dData, e.target.checked);
                               }}/>
                        <label className={"form-check-label"} htmlFor={"sound-on-checkbox"}>Mute Sounds</label>
                    </div>
                </div>
            }
            {!fullscreen && <CampusDropDown campusId={campusId} setCampusId={(item) => setCampusId(item)}
                            setCount={(item) => {setCount(item)}} pageAction={(e) => {
                                if (dData?.campusData[campusId.toString()]?.is_cloud !== dData?.campusData[e.target.value]?.is_cloud){
                                    if (dData?.campusData[e.target.value]?.is_cloud){
                                        window.location.href = "/emergency";
                                    }else{
                                        window.location.href = "/alerts";
                                    }
                                }
            }}/>}
        </div>
    );
    return (
        <>
            {dData?.migrated && <SessionReload />}
            {!fullscreen && <Navigation/>}
            <div className={"main-content"}>
                <Header preTitle={"Nurse Call"} Title={"Emergency"} content={headerContent}
                        fullscreen={fullscreen} subTitle={fullscreen ? "" : ""}/>
                <div className={"container-fluid"}>
                    <AlertSyncStatus
                        isConnected={isConnected}
                        isDataStale={isDataStale}
                        hasAlerts={(activeCloudAlarms?.length ?? 0) > 0}
                        connectionError={connectionError}
                    />
                    {/*{!dData?.campusData[campusId.toString()]?.soundsOff &&*/}
                    {/*    <div className={"form-check mb-3"}>*/}
                    {/*        <input className={"form-check-input table-checkbox"} type={"checkbox"} checked={soundOff}*/}
                    {/*               name={"sound-on-checkbox"} id={"sound-on-checkbox"}*/}
                    {/*               style={{width: "20px", height: "20px"}}*/}
                    {/*               onChange={(e) => {*/}
                    {/*                   setSoundOff(e.target.checked);*/}
                    {/*                   setSoundsMute(dData, e.target.checked);*/}
                    {/*               }}/>*/}
                    {/*        <label className={"form-check-label"} htmlFor={"sound-on-checkbox"}>Mute Sounds</label>*/}
                    {/*    </div>*/}
                    {/*}*/}
                    <div className={"col"}>
                        <ul className={"nav nav-tabs nav-overflow header-tabs"}>
                            <li key={"set-emerg"} className={"nav-item"}>
                                <button className={tabItem === "emergency" ? "nav-link active" : "nav-link"}
                                        onClick={() => setTabItem("emergency")}>
                                    Emergency
                                    <span className={"position-relative translate-middle badge rounded-pill bg-danger"}
                                      style={{left: "15px"}}>{activeCloudAlarms?.length > 0 ? activeCloudAlarms.length : ""}</span>
                                </button>
                            </li>
                            <li key={"set-lowbat"} className={"nav-item"}>
                                <button className={tabItem === "lowbat" ? "nav-link active" : "nav-link"}
                                        onClick={() => setTabItem("lowbat")}>
                                    Batteries
                                    <span className={"position-relative translate-middle badge rounded-pill bg-primary"}
                                      style={{left: "15px"}}>{lowBatData?.length > 0 ? lowBatData.length : ""}</span>
                                </button>
                            </li>
                            <li key={"set-lost"} className={"nav-item"}>
                                <button className={tabItem === "lost" ? "nav-link active" : "nav-link"}
                                        onClick={() => setTabItem("lost")}>
                                    Communication Lost
                                    <span className={"position-relative translate-middle badge rounded-pill bg-primary"}
                                      style={{left: "15px"}}>{lostData?.length > 0 ? lostData.length : ""}</span>
                                </button>
                            </li>
                            <li key={"set-other"} className={"nav-item"}>
                                <button className={tabItem === "other" ? "nav-link active" : "nav-link"}
                                        onClick={() => setTabItem("other")}>
                                    Other
                                    <span className={"position-relative translate-middle badge rounded-pill bg-primary"}
                                      style={{left: "15px"}}>{otherData?.length > 0 ? otherData.length : ""}</span>
                                </button>
                            </li>
                        </ul>
                    </div>
                    <div className={"row"}>
                        <div className={tabItem === "init" ? "d-block" : "d-none"}>
                            <TableSkeleton />
                        </div>
                        <div className={tabItem === "emergency" ? "d-block" : "d-none"}>
                            <table className={"table table-sm table-white table-hover"}
                                   style={{borderCollapse: "collapse"}}>
                                <thead className={isBannerPresent() ? "sticky-table-headX" : "sticky-table-head"}>
                                {tableHeaders}
                                </thead>
                                <tbody>
                                {emergRows}
                                </tbody>
                            </table>
                        </div>
                        <div className={tabItem === "lowbat" ? "d-block" : "d-none"}>
                            <table className={"table table-sm table-white table-hover"}
                                   style={{borderCollapse: "collapse"}}>
                                <thead className={isBannerPresent() ? "sticky-table-headX" : "sticky-table-head"}>
                                {tableMaintHeaders}
                                </thead>
                                <tbody>
                                {lowBatRows}
                                </tbody>
                            </table>
                        </div>
                        <div className={tabItem === "lost" ? "d-block" : "d-none"}>
                            <table className={"table table-sm table-white table-hover"}
                                   style={{borderCollapse: "collapse"}}>
                                <thead className={isBannerPresent() ? "sticky-table-headX" : "sticky-table-head"}>
                                {tableMaintHeaders}
                                </thead>
                                <tbody>
                                {lostRows}
                                </tbody>
                            </table>
                        </div>
                        <div className={tabItem === "other" ? "d-block" : "d-none"}>
                            <table className={"table table-sm table-white table-hover"}
                                   style={{borderCollapse: "collapse"}}>
                                <thead className={isBannerPresent() ? "sticky-table-headX" : "sticky-table-head"}>
                                {tableMaintHeaders}
                                </thead>
                                <tbody>
                                {otherRows}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className={"footer-spacer"}>

                    </div>
                </div>
                <Footer/>
                <Suspense fallback={"Loading..."}>
                    <ToastContainer position={"bottom-right"} theme={"colored"} newestOnTop={true} pauseOnHover={true}
                                    autoClose={10000} closeOnClick={true}/>
                </Suspense>
            </div>
        </>
    );
};

export default CurrentCloudAlerts;